import React, { useEffect, useId, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { AlertOctagon, AlertTriangle, CheckCircle2, Clock, FileText, Info, Loader2, ShieldAlert, XCircle } from 'lucide-react';
import { POC_DISCLAIMER, statusLabel } from './pocFormat';
import { MESSAGES } from '../../services/pocApi';

// Shared building blocks for the POC claim and warranty administration screens,
// in the existing ICMS style (square corners, uppercase tracking, #DCE4EE borders).

const STATUS_STYLES = {
    ACCEPTED: { icon: CheckCircle2, cls: 'bg-[#E8F5EC] text-[#14532D] border-[#1F7A3F]' },
    REJECTED: { icon: XCircle, cls: 'bg-[#FBEAEA] text-danger border-danger' },
    MANUAL_REVIEW: { icon: AlertTriangle, cls: 'bg-[#FFF4DB] text-[#6B4200] border-[#B7791F]' },
    PENDING_EVALUATION: { icon: Clock, cls: 'bg-[#E8F0FB] text-[#1E3A8A] border-[#3B6FB6]' },
    EVALUATION_ERROR: { icon: AlertOctagon, cls: 'bg-[#2B2B2B] text-white border-[#2B2B2B]' },
    ACTIVE: { icon: CheckCircle2, cls: 'bg-[#E8F5EC] text-[#14532D] border-[#1F7A3F]' },
    DRAFT: { icon: FileText, cls: 'bg-[#F2F2F2] text-[#333333] border-[#999999]' },
    RETIRED: { icon: Clock, cls: 'bg-white text-text-muted border-line-strong' },
};

export function StatusBadge({ status, size = 'sm' }) {
    const style = STATUS_STYLES[status] || { icon: Info, cls: 'bg-white text-text-main border-[#999999]' };
    const Icon = style.icon;
    const sizing = size === 'lg' ? 'text-sm px-4 py-2' : 'text-[10px] px-2 py-1';
    return (
        <span className={`inline-flex items-center gap-1.5 border font-semibold uppercase tracking-widest whitespace-nowrap ${sizing} ${style.cls}`}>
            <Icon className={size === 'lg' ? 'w-4 h-4' : 'w-3 h-3'} aria-hidden="true" />
            {statusLabel(status)}
        </span>
    );
}

export function Panel({ title, actions, children, className = '' }) {
    return (
        <section className={`bg-surface border border-line ${className}`} aria-label={typeof title === 'string' ? title : undefined}>
            {(title || actions) && (
                <div className="px-6 py-4 border-b border-line flex flex-wrap items-center justify-between gap-3">
                    {title && <h3 className="text-xs font-light text-text-main uppercase tracking-widest">{title}</h3>}
                    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
                </div>
            )}
            <div className="p-6">{children}</div>
        </section>
    );
}

export function PageHeader({ title, subtitle, actions }) {
    return (
        <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
                <h1 className="text-lg font-light uppercase tracking-widest text-text-main">{title}</h1>
                {subtitle && <p className="text-xs text-text-muted mt-2 max-w-3xl">{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
        </div>
    );
}

export function Disclaimer() {
    return (
        <p className="flex items-start gap-2 text-[11px] text-text-muted border-l-2 border-[#B7791F] pl-3 py-1" data-testid="poc-disclaimer">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" aria-hidden="true" />
            <span>{POC_DISCLAIMER}</span>
        </p>
    );
}

export function LoadingState({ label = 'Loading…' }) {
    return (
        <div role="status" aria-live="polite" className="flex items-center gap-3 text-xs uppercase tracking-widest text-text-muted py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            {label}
        </div>
    );
}

export function EmptyState({ children }) {
    return <p className="text-sm text-text-muted py-8 text-center">{children}</p>;
}

/** Shows an ApiError's user-safe message; never the raw server payload. */
export function ErrorNotice({ error, onRetry, title }) {
    if (!error) return null;
    const unavailable = error.kind === 'unavailable';
    const Icon = error.kind === 'forbidden' ? ShieldAlert : AlertOctagon;
    return (
        <div role="alert" className="border border-danger bg-[#FBEAEA] text-[#5C0000] p-4 text-sm flex items-start gap-3">
            <Icon className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
            <div className="space-y-2 min-w-0">
                <p className="font-semibold">{title || (unavailable ? 'Service unavailable' : error.kind === 'forbidden' ? 'Access denied' : 'Request failed')}</p>
                <p>{unavailable ? MESSAGES.unavailable : error.message}</p>
                {error.issues?.length > 0 && <IssueList issues={error.issues} />}
                {onRetry && (
                    <button type="button" className="btn btn-secondary !px-4 !py-2" onClick={onRetry}>Try again</button>
                )}
            </div>
        </div>
    );
}

export function IssueList({ issues }) {
    return (
        <ul className="space-y-1 text-xs">
            {issues.map((issue, i) => (
                <li key={`${issue.code}-${i}`} className="flex flex-wrap gap-2">
                    <code className="font-semibold">{issue.code}</code>
                    {issue.severity && <span className="uppercase tracking-widest text-[10px]">[{issue.severity}]</span>}
                    {issue.field && <span className="text-text-muted">{issue.field}:</span>}
                    <span>{issue.message}</span>
                </li>
            ))}
        </ul>
    );
}

export function AccessDenied({ what = 'this page' }) {
    return (
        <div role="alert" className="border border-line bg-surface p-8 text-center space-y-3">
            <ShieldAlert className="w-6 h-6 mx-auto text-text-muted" aria-hidden="true" />
            <h2 className="text-sm uppercase tracking-widest">Access denied</h2>
            <p className="text-sm text-text-muted">Administrator access (App.ADMIN) is required for {what}.</p>
        </div>
    );
}

/** Label + control + hint + error, with the error associated to the control for screen readers. */
export function Field({ label, hint, error, required, children, className = '' }) {
    const id = useId();
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const control = React.Children.only(children);
    return (
        <div className={className}>
            <label htmlFor={id} className="block text-xs font-semibold text-text-muted uppercase tracking-widest mb-2">
                {label}{required && <span aria-hidden="true"> *</span>}
            </label>
            {React.cloneElement(control, {
                id,
                'aria-invalid': error ? true : undefined,
                'aria-required': required || undefined,
                'aria-describedby': [hintId, errorId].filter(Boolean).join(' ') || undefined,
                className: `${control.props.className || 'form-input'}${error ? ' !border-danger' : ''}`,
            })}
            {hint && <p id={hintId} className="text-[11px] text-text-muted mt-1">{hint}</p>}
            {error && <p id={errorId} className="text-[11px] text-danger mt-1 font-semibold">{error}</p>}
        </div>
    );
}

export function DetailList({ items }) {
    return (
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4">
            {items.map(([term, value]) => (
                <div key={term} className="min-w-0">
                    <dt className="text-[10px] font-semibold uppercase tracking-widest text-text-muted">{term}</dt>
                    <dd className="text-sm text-text-main mt-1 break-words">{value ?? '—'}</dd>
                </div>
            ))}
        </dl>
    );
}

/** Accessible modal confirmation. Escape and Cancel close it unless an action is in progress. */
export function ConfirmDialog({ open, title, children, confirmLabel = 'Confirm', danger, busy, onConfirm, onCancel }) {
    const titleId = useId();
    const confirmRef = useRef(null);
    useEffect(() => {
        if (open) confirmRef.current?.focus();
    }, [open]);
    if (!open) return null;
    const onKeyDown = (e) => {
        if (e.key === 'Escape' && !busy) onCancel();
    };
    return (
        <div className="fixed inset-0 bg-primary/70 z-50 flex items-center justify-center p-4" onKeyDown={onKeyDown}>
            <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-surface border border-line w-full max-w-lg">
                <div className="px-6 py-4 border-b border-line">
                    <h2 id={titleId} className="text-sm uppercase tracking-widest">{title}</h2>
                </div>
                <div className="p-6 text-sm space-y-3">{children}</div>
                <div className="px-6 py-4 border-t border-line flex justify-end gap-3">
                    <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
                    <button ref={confirmRef} type="button" className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm} disabled={busy}>
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>
    );
}

const tabClass = ({ isActive }) =>
    `text-[11px] uppercase tracking-widest font-semibold px-4 py-2 border-b-2 transition-colors ${
        isActive ? 'border-primary text-text-main' : 'border-transparent text-text-muted hover:text-text-main'
    }`;

/** Secondary tabs inside the POC Claims and Warranty Admin areas. */
export function SubNav({ label, links }) {
    return (
        <nav aria-label={label} className="flex flex-wrap gap-2 border-b border-line">
            {links.map((link) => (
                <NavLink key={link.to} to={link.to} end className={tabClass}>{link.label}</NavLink>
            ))}
        </nav>
    );
}

export const POC_LINKS = [
    { to: '/poc-claims', label: 'Claim Dashboard' },
    { to: '/poc-claims/new', label: 'Submit Claim' },
    { to: '/poc-claims/history', label: 'Claim History' },
];

export const ADMIN_LINKS = [
    { to: '/admin/warranty-policies', label: 'Warranty Policies' },
    { to: '/admin/authorization-rules', label: 'Authorization Rules' },
];
